import { useCallback, useEffect, useState } from "react";
import { useGetRevenueMachineRenderMothApi } from "../../hooks/revenue/useGetRevenueMachineRenderMothApi";
import dayjs, { Dayjs } from "dayjs"; // Import dayjs
import { Badge, Calendar, Modal, Select, Spin } from "antd";
import { formatNumberWithCommas, formatToVND } from "../../helper";
import { useLocation } from "react-router-dom";
import {
  LoadingOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { CardItemRevenueStore, FooterFilterMobile } from "../../components";
import { getRevenueMachineApi } from "../../services";

const convertMonthNameToNumber = (monthName: string): number | null => {
  const monthMap: { [key: string]: number } = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };

  // Convert input to lowercase to handle case variations
  const normalizedMonthName = monthName.toLowerCase();

  // Return the corresponding month number or null if not found
  return monthMap[normalizedMonthName] || null;
};

const RenvenueCalendar = () => {
  const [monthSelect, setMonthSelect] = useState<Dayjs>(dayjs()); // Use dayjs for current month
  const [startDate, setStartDate] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listRevenueMachine, setListRevenueMachine] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [dataRevenue, setDataRevenue] = useState<any>();
  const [dataRevenueItem, setDataRevenueItem] = useState<any>();

  const location = useLocation();
  const storeCode = location.state?.storeCode;

  const { revenueMachineRenderMonth } = useGetRevenueMachineRenderMothApi({
    monthSelect: monthSelect.format("YYYY-MM-DD"), // Format for API
    storeCode,
  });

  const months = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  useEffect(() => {
    if (revenueMachineRenderMonth?.data) {
      const dataConfig = {
        couponPrice: revenueMachineRenderMonth?.data?.couponPrice,
        f1Counter: revenueMachineRenderMonth?.data?.f1Counter,
        groupName: revenueMachineRenderMonth?.data?.groupName,
        quantity: revenueMachineRenderMonth?.data?.quantity || 0,
        revenueTotal: revenueMachineRenderMonth?.data?.revenueTotal || 0,
        totalCoupon: revenueMachineRenderMonth?.data?.totalCoupon,
        totalF1: revenueMachineRenderMonth?.data?.totalF1,
        userGroup: revenueMachineRenderMonth?.data?.userGroup,
      };
      setDataRevenue(dataConfig);
    }
  }, [revenueMachineRenderMonth?.data]);

  useEffect(() => {
    if (listRevenueMachine) {
      const dataConfig = {
        couponPrice: listRevenueMachine?.couponPrice,
        f1Counter: listRevenueMachine?.f1Counter,
        groupName: listRevenueMachine?.groupName,
        quantity: listRevenueMachine?.quantity || 0,
        revenueTotal: listRevenueMachine?.revenueTotal || 0,
        totalCoupon: listRevenueMachine?.totalCoupon,
        totalF1: listRevenueMachine?.totalF1,
        userGroup: listRevenueMachine?.userGroup,
      };
      setDataRevenueItem(dataConfig);
    }
  }, [listRevenueMachine]);

  useEffect(() => {
    (async () => {
      if (startDate) {
        setLoading(true);
        const res = await getRevenueMachineApi(storeCode, startDate, startDate);
        setLoading(false);
        setListRevenueMachine(res?.data);
      }
    })();
  }, [startDate, storeCode]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle navigation: Previous month
  const handlePrevMonth = () => {
    setMonthSelect((prev) => prev.subtract(1, "month"));
  };

  // Handle navigation: Next month
  const handleNextMonth = () => {
    setMonthSelect((prev) => prev.add(1, "month"));
  };

  const years = Array.from({ length: 10 }, (_, i) =>
    (dayjs().year() - 5 + i).toString()
  );

  // Hàm xử lý khi thay đổi tháng
  const handleMonthChange = (value: number) => {
    setMonthSelect((prev) => prev.set("month", value));
  };

  // Hàm xử lý khi thay đổi năm
  const handleYearChange = (value: string) => {
    setMonthSelect((prev) => prev.set("year", parseInt(value)));
  };

  const cellRender = useCallback(
    (current: Dayjs, info: any) => {
      if (info.type === "date") {
        const formattedDate = current.format("YYYY-MM-DD");
        const dateData = revenueMachineRenderMonth?.data?.dateResponses?.find(
          (item: any) => item.date === formattedDate
        );

        if (!dateData) {
          return null;
        }

        return (
          <>
            <ul
              onClick={() => {
                showModal();
                const month = current.month() + 1; // Month is 0-based
                const day = current.date();
                const formattedMonth = month < 10 ? `0${month}` : `${month}`;
                const formattedDay = day < 10 ? `0${day}` : `${day}`;
                setStartDate(
                  `${current.year()}-${formattedMonth}-${formattedDay}`
                );
              }}
              className="events max-sm:hidden bg-[#FFD6FA] p-[8px] rounded-md"
            >
              <li className="xl:pl-2">
                <Badge
                  status="success"
                  text={`Revenue: ${formatNumberWithCommas(dateData.revenue)}`}
                />
              </li>
              <li className="xl:pl-2">
                <Badge
                  status="processing"
                  text={`Quantity: ${dateData.quantity}`}
                />
              </li>
              <li className="xl:pl-2">
                <Badge status="warning" text={`F1: ${dateData.f1}`} />
              </li>
            </ul>
            <div
              onClick={() => {
                showModal();
                const month = current.month() + 1;
                const day = current.date();
                const formattedMonth = month < 10 ? `0${month}` : `${month}`;
                const formattedDay = day < 10 ? `0${day}` : `${day}`;
                setStartDate(
                  `${current.year()}-${formattedMonth}-${formattedDay}`
                );
              }}
              className="sm:hidden flex flex-col"
            >
              <small className="text-[8px] text-photo-time-pink-main">
                {formatNumberWithCommas(dateData.revenue)}
              </small>
              <small className="text-[8px] text-green-500">
                {dateData.quantity}
              </small>
              <small className="text-[8px] text-red-700">{dateData.f1}</small>
            </div>
          </>
        );
      }
      return info.originNode;
    },
    [revenueMachineRenderMonth]
  );

  return (
    <>
      <Modal
        footer={null}
        width="86vw"
        bodyStyle={{
          height: "90vh",
          padding: 0,
          overflow: "auto",
          margin: 0,
        }}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        className="xl:-mt-[10vh] no-scrollbar"
      >
        {loading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Spin
              className="text-pink-500"
              indicator={<LoadingOutlined color="pink" spin />}
            />
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            {dataRevenueItem && (
              <CardItemRevenueStore
                isShowTotal={true}
                isShowBtn={false}
                data={dataRevenueItem}
              ></CardItemRevenueStore>
            )}

            <table className="w-full">
              <tr className="pt-1 lg:grid lg:grid-cols-11 hidden bg-opacity-25 h-[4vh] text-gray-600 items-center w-full">
                <th className="text-[10px] sm:text-sm lg:text-base">
                  MACHINE ID
                </th>
                <th className="text-[10px] sm:text-sm">NAME</th>
                <th className="text-[10px] sm:text-sm">REVENUE</th>
                <th className="text-[10px] sm:text-sm">COUPON</th>
                <th className="text-[10px] sm:text-sm">TOTAL INCOME</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">10K</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">20K</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">30K</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">50K</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">70K</th>
                <th className="max-lg:hidden text-[10px] sm:text-sm">100K</th>
              </tr>
              {listRevenueMachine?.dateResponses?.length > 0 &&
                listRevenueMachine?.dateResponses?.map(
                  (item: any, index: number) => (
                    <div
                      key={index}
                      className="lg:bg-transparent w-full bg-gray-300 bg-opacity-15 lg:px-[12px] rounded-xl"
                    >
                      <div className="w-full">
                        <tr className="grid grid-cols-5 lg:hidden bg-photo-time-pink-light bg-opacity-25 h-[4vh] text-gray-600 items-center w-full">
                          <th className="text-[10px] sm:text-sm">MACHINE ID</th>
                          <th className="text-[10px] sm:text-sm">NAME</th>
                          <th className="text-[10px] sm:text-sm">REVENUE</th>
                          <th className="text-[10px] sm:text-sm">COUPON</th>
                          <th className="text-[10px] sm:text-sm">
                            TOTAL INCOME
                          </th>
                        </tr>
                      </div>
                      <div>
                        <tr
                          className={`grid lg:grid-cols-11 ${
                            index % 2 === 0 && "bg-gray-300 bg-opacity-20"
                          } lg:-gap-20 lg:py-6 grid-cols-5 my-2`}
                        >
                          <td className="text-center text-[10px] sm:text-sm flex justify-center items-center">
                            {item?.userId}
                          </td>
                          <td className="flex flex-col justify-center text-[10px] sm:text-sm items-center md:gap-2 gap-1">
                            {item?.userName}
                          </td>
                          <tr className="flex flex-col justify-center text-[10px] sm:text-sm items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.countMon}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.totMon)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center text-[10px] sm:text-sm items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.totalCouponMonth)}
                            </td>
                          </tr>
                          <td className="flex flex-col justify-center text-[10px] text-green-500 font-semibold sm:text-sm lg:text-base items-center md:gap-2 gap-1">
                            {formatNumberWithCommas(
                              item?.totMon - item?.totalCouponMonth
                            )}
                          </td>
                          <tr className="flex text-center max-lg:hidden flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon10}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon10)}
                            </td>
                          </tr>
                          <tr className="flex text-center max-lg:hidden flex-col justify-center items-center md:gap-2-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon20}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon20)}
                            </td>
                          </tr>
                          <tr className="flex max-lg:hidden flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon30}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon30)}
                            </td>
                          </tr>
                          <tr className="flex max-lg:hidden flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon50}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon50)}
                            </td>
                          </tr>
                          <tr className="flex max-lg:hidden flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon70}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon70)}
                            </td>
                          </tr>
                          <tr className="flex max-lg:hidden flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm">
                              {item?.totalCoupon100}
                            </td>
                            <td className="text-[10px] sm:text-sm text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon100)}
                            </td>
                          </tr>
                        </tr>
                      </div>
                      <div>
                        <tr className="grid grid-cols-6 lg:hidden bg-yellow-300 bg-opacity-25 h-[4vh] text-gray-600 items-center w-full">
                          <th className="text-[10px] sm:text-sm">10K</th>
                          <th className="text-[10px] sm:text-sm lg:text-base">
                            20K
                          </th>
                          <th className="text-[10px] sm:text-sm lg:text-base">
                            30K
                          </th>
                          <th className="text-[10px] sm:text-sm lg:text-base">
                            50K
                          </th>
                          <th className="text-[10px] sm:text-sm lg:text-base">
                            70K
                          </th>
                          <th className="text-[10px] sm:text-sm lg:text-base">
                            100K
                          </th>
                        </tr>
                      </div>
                      <div className="w-full">
                        <tr className="grid mb-9 lg:hidden grid-cols-6 my-2">
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon10}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon10)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon20}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon20)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon30}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon30)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon50}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon50)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon70}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon70)}
                            </td>
                          </tr>
                          <tr className="flex flex-col justify-center items-center md:gap-2 gap-1">
                            <td className="text-[10px] sm:text-sm lg:text-base">
                              {item?.totalCoupon100}
                            </td>
                            <td className="text-[10px] sm:text-sm lg:text-base text-green-500 font-semibold">
                              {formatNumberWithCommas(item?.countCoupon100)}
                            </td>
                          </tr>
                        </tr>
                      </div>
                    </div>
                  )
                )}
            </table>
          </div>
        )}
      </Modal>
      <div className="w-full">
        <div className="w-[100%] items-center md:pb-2 font-semibold text-ok-travel-green flex lg:pb-0 justify-between mb-2 border-b-[1.5px] md:mb-4">
          <h3 className="uppercase max-md:mt-4 max-sm:py-2 px-[16px] md:px-[0px] text-medium text-gray-500 md:text-md xl:text-xl">
            Manage Revenue
          </h3>
        </div>
        {dataRevenue && (
          <CardItemRevenueStore
            isShowTotal={false}
            isShowBtn={false}
            data={dataRevenue}
          ></CardItemRevenueStore>
        )}
        <div className="flex items-center justify-end xl:gap-6 lg:gap-4 gap-1 px-[16px] mb-4">
          <button
            onClick={handlePrevMonth}
            className="bg-gray-200 xl:p-2 p-1 rounded hover:bg-gray-300 max-sm:text-sm"
          >
            <LeftOutlined />
          </button>

          <div className="flex w-fit items-center max-sm:w-full max-sm:justify-center relative 2xl:gap-6 xl:gap-5 lg:gap-4 gap-3">
            {/* Dropdown tùy chỉnh cho tháng */}
            <div className="lg:w-[5vw] w-[30vw]">
              <button
                onClick={() => {
                  setMonthDropdownOpen(!monthDropdownOpen);
                  if (yearDropdownOpen) {
                    setYearDropdownOpen(false);
                  }
                }}
                className="w-full text-sm bg-white border border-gray-300 rounded p-1 text-center justify-center flex items-center"
              >
                <span>
                  {convertMonthNameToNumber(months[monthSelect.month()].label)}
                </span>
                {/* <span>{monthSelect.year()}</span> */}
              </button>
              {monthDropdownOpen && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg mt-1">
                  <div className="grid grid-cols-3 gap-1 p-1">
                    {months.map((month, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setMonthSelect(monthSelect.set("month", month.value));
                          setMonthDropdownOpen(false);
                        }}
                        className={`p-1 text-center hover:bg-gray-100 rounded text-sm ${
                          monthSelect.month() === month.value
                            ? "bg-photo-time-pink-light text-white"
                            : ""
                        }`}
                      >
                        {month.label.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Dropdown tùy chỉnh cho năm */}
            <div className="lg:w-[5vw] w-[30vw]">
              <button
                onClick={() => {
                  setYearDropdownOpen(!yearDropdownOpen);
                  if (monthDropdownOpen) {
                    setMonthDropdownOpen(false);
                  }
                }}
                className="w-full bg-white border border-gray-300 rounded p-1 text-sm text-center justify-center"
              >
                {monthSelect.year()}
              </button>
              {yearDropdownOpen && (
                <div className="absolute right-0 z-10 w-full bg-white border border-gray-300 rounded shadow-lg mt-1">
                  <div className="grid grid-cols-2 gap-1 p-1">
                    {years.map((year) => (
                      <button
                        key={year}
                        onClick={() => {
                          setMonthSelect(
                            monthSelect.set("year", parseInt(year))
                          );
                          setYearDropdownOpen(false);
                        }}
                        className={`p-1 text-center hover:bg-gray-100 rounded text-sm ${
                          monthSelect.year() === parseInt(year)
                            ? "bg-photo-time-pink-light text-white"
                            : ""
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleNextMonth}
            className="bg-gray-200 xl:p-2 p-1 rounded hover:bg-gray-300 max-sm:text-sm"
          >
            <RightOutlined />
          </button>
        </div>
        <Calendar
          className="xl:px-[16px]"
          value={monthSelect}
          onPanelChange={(data: Dayjs) => {
            setMonthSelect(data);
          }}
          cellRender={cellRender}
          headerRender={() => null}
        />
        <div className="flex lg:hidden pl-3 mt-2 justify-center flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="bg-photo-time-pink-main w-4 h-4"></div>
            <span className="text-sm">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-green-600 w-4 h-4"></div>
            <span className="text-sm">Quantity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-red-500 w-4 h-4"></div>
            <span className="text-sm">F1</span>
          </div>
        </div>
      </div>
      <FooterFilterMobile isFilter={true} />
    </>
  );
};

export default RenvenueCalendar;
